// src/components/dashboard/admin/AdminContacts.tsx
// Admin view for incoming contact form submissions.
import React, { useEffect, useState, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminList, AdminListRow } from './AdminList';
import { IconChip } from '@/components/custom-ui/IconChip';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Mail, RefreshCw, ExternalLink, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OutlineButton, EmptyState, DeleteConfirmDialog } from '@/components/custom-ui';
import { useToast } from '@/hooks/use-toast';
import { adminApi } from '@/lib/api/adminApi';
import {
    Contact, ContactStatus, ContactStatusLabels, ContactTypeLabels,
} from '@/lib/models/contact';

const STATUS_FILTER_OPTIONS: Array<{ value: 'all' | ContactStatus; label: string }> = [
    { value: 'all', label: 'Alla statusar' },
    { value: 'new', label: ContactStatusLabels.new },
    { value: 'contacted', label: ContactStatusLabels.contacted },
    { value: 'closed', label: ContactStatusLabels.closed },
];

const STATUS_BADGE_VARIANT: Record<ContactStatus, 'default' | 'secondary' | 'outline'> = {
    new: 'default',
    contacted: 'secondary',
    closed: 'outline',
};

const formatDate = (iso: string): string => {
    try {
        return new Date(iso).toLocaleString('sv-SE', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return iso;
    }
};

const AdminContacts: React.FC = () => {
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<'all' | ContactStatus>('all');
    const [selected, setSelected] = useState<Contact | null>(null);
    const [updatingStatusFor, setUpdatingStatusFor] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { toast } = useToast();

    const load = useCallback(async (filter: 'all' | ContactStatus) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await adminApi.listContacts(filter === 'all' ? undefined : filter);
            // Defensive: some backends may serialize empty results as null.
            const list = Array.isArray(result) ? result : [];
            // Newest first — backend default sort may vary across endpoints.
            const sorted = [...list].sort((a, b) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            setContacts(sorted);
        } catch (err) {
            console.error('[AdminContacts] load failed:', err);
            setError('Kunde inte hämta kontaktförfrågningar.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        load(statusFilter);
    }, [statusFilter, load]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            await adminApi.deleteContact(deleteTarget.id);
            setContacts((prev) => prev.filter((c) => c.id !== deleteTarget.id));
            if (selected?.id === deleteTarget.id) {
                setSelected(null);
            }
            toast({
                title: 'Förfrågan raderad',
                description: `${deleteTarget.name}s meddelande har tagits bort.`,
            });
            setDeleteTarget(null);
        } catch (err) {
            console.error('[AdminContacts] delete failed:', err);
            toast({
                title: 'Kunde inte radera',
                description: 'Försök igen om en stund.',
                variant: 'destructive',
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleStatusChange = async (contact: Contact, newStatus: ContactStatus) => {
        if (newStatus === contact.status) return;
        setUpdatingStatusFor(contact.id);
        try {
            const updated = await adminApi.updateContactStatus(contact.id, newStatus);
            setContacts((prev) => prev.map((c) => (c.id === contact.id ? updated : c)));
            if (selected?.id === contact.id) {
                setSelected(updated);
            }
        } catch (err) {
            console.error('[AdminContacts] status update failed:', err);
            setError('Kunde inte uppdatera status.');
        } finally {
            setUpdatingStatusFor(null);
        }
    };

    return (
        <div className="space-y-6">
            {/* Title is in the top header strip (TopHeader). #358 — was a six-column
                <Table>, the only one in the admin dashboard. Now the same AdminList
                shell as the other two, with the per-row status control and delete
                kept in the trailing slot. Date and municipality moved into the
                subtitle: they are context for scanning, not columns to compare. */}
            <AdminList
                title="Kontaktförfrågningar"
                count={contacts.length}
                isLoading={isLoading}
                filter={
                    <Select
                        value={statusFilter}
                        onValueChange={(v) => setStatusFilter(v as 'all' | ContactStatus)}
                    >
                        <SelectTrigger className="w-[170px]">
                            <SelectValue placeholder="Filtrera status" />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUS_FILTER_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                }
                action={
                    <OutlineButton onClick={() => load(statusFilter)} disabled={isLoading} icon={RefreshCw}>
                        Uppdatera
                    </OutlineButton>
                }
                empty={
                    <EmptyState
                        icon={Mail}
                        title="Inga förfrågningar"
                        description="Inga kontaktförfrågningar matchar det valda filtret."
                    />
                }
            >
                {contacts.map((contact) => (
                    <AdminListRow
                        key={contact.id}
                        onClick={() => setSelected(contact)}
                        leading={<IconChip icon={Mail} tone={contact.status === 'new' ? 'amber' : 'neutral'} />}
                        title={contact.name}
                        subtitle={
                            <>
                                {contact.email}
                                {contact.municipality && ` · ${contact.municipality}`}
                                {` · ${formatDate(contact.created_at)}`}
                            </>
                        }
                        trailing={
                            <>
                                <Badge variant="outline" className="hidden md:inline-flex">
                                    {ContactTypeLabels[contact.type]}
                                </Badge>
                                <Select
                                    value={contact.status}
                                    onValueChange={(v) => handleStatusChange(contact, v as ContactStatus)}
                                    disabled={updatingStatusFor === contact.id}
                                >
                                    <SelectTrigger className="h-8 w-[130px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(['new', 'contacted', 'closed'] as ContactStatus[]).map((st) => (
                                            <SelectItem key={st} value={st}>{ContactStatusLabels[st]}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => setDeleteTarget(contact)}
                                    aria-label={`Radera meddelande från ${contact.name}`}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </>
                        }
                    />
                ))}
            </AdminList>

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    {error}
                </div>
            )}

            {/* Detail dialog */}
            <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
                <DialogContent className="max-w-2xl">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle>{selected.name}</DialogTitle>
                                <DialogDescription>
                                    {ContactTypeLabels[selected.type]} · {formatDate(selected.created_at)}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 text-sm">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-xs font-semibold text-muted-foreground mb-1">E-post</div>
                                        <a
                                            href={`mailto:${selected.email}`}
                                            className="text-[#1A4D4F] hover:underline break-all"
                                        >
                                            {selected.email}
                                        </a>
                                    </div>
                                    <div>
                                        <div className="text-xs font-semibold text-muted-foreground mb-1">Kommun</div>
                                        <div>{selected.municipality}</div>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-muted-foreground mb-1">Status</div>
                                    <Select
                                        value={selected.status}
                                        onValueChange={(v) => handleStatusChange(selected, v as ContactStatus)}
                                        disabled={updatingStatusFor === selected.id}
                                    >
                                        <SelectTrigger className="w-[180px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {(['new', 'contacted', 'closed'] as ContactStatus[]).map((s) => (
                                                <SelectItem key={s} value={s}>
                                                    {ContactStatusLabels[s]}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-muted-foreground mb-1">Meddelande</div>
                                    <div className="rounded-lg bg-muted/50 p-3 whitespace-pre-wrap">
                                        {selected.message?.trim() || (
                                            <span className="text-muted-foreground italic">
                                                Inget meddelande angivet
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground pt-2 border-t">
                                    <div>
                                        ID: <code className="font-mono">{selected.id}</code>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => setDeleteTarget(selected)}
                                    >
                                        <Trash2 className="h-4 w-4 mr-1.5" />
                                        Radera meddelande
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete confirmation — type the name to confirm */}
            <DeleteConfirmDialog
                open={!!deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                title="Radera kontaktförfrågan"
                entityLabel="meddelandet från"
                entityName={deleteTarget?.name || ''}
                cascadeWarning="Meddelandet tas bort permanent från databasen."
                onConfirm={handleDelete}
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default AdminContacts;
